import React, { useState, useMemo, useEffect } from 'react';

// ── Advance-tax date helper ───────────────────────────────────────────────────
function getUpcomingAdvanceTaxDate() {
  const now = new Date();
  const yr = now.getFullYear();
  const dates = [
    { d: new Date(yr, 5, 15), installment: '15%', due: `15 June ${yr}` },
    { d: new Date(yr, 8, 15), installment: '45%', due: `15 Sept ${yr}` },
    { d: new Date(yr, 11, 15), installment: '75%', due: `15 Dec ${yr}` },
    { d: new Date(yr + 1, 2, 15), installment: '100%', due: `15 March ${yr + 1}` },
  ];
  for (const item of dates) {
    const daysUntil = Math.ceil((item.d - now) / 86400000);
    if (daysUntil >= 0 && daysUntil <= 60) return { ...item, daysUntil };
  }
  return null;
}

const fmtRs = n => `Rs.\u200b${Math.round(Math.abs(n)).toLocaleString('en-IN')}`;

const AffordabilityEngine = ({ user, navigateTo, goBack }) => {
  // ── Purchase details ──────────────────────────────────────────────────────
  const [purchaseName, setPurchaseName] = useState('');
  const [amount, setAmount] = useState('');
  const [purchaseType, setPurchaseType] = useState('one-time'); // 'one-time' | 'emi'
  const [emiAmount, setEmiAmount] = useState('');
  const [emiMonths, setEmiMonths] = useState('');
  const [timing, setTiming] = useState('now'); // 'now' | 'next-month' | '3plus'

  // ── Financial context ─────────────────────────────────────────────────────
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [fixedObligations, setFixedObligations] = useState('');
  const [variableExpenses, setVariableExpenses] = useState('');
  const [liquidSavings, setLiquidSavings] = useState('');
  const [upcomingBigExpense, setUpcomingBigExpense] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const [mode, setMode] = useState('input'); // 'input' | 'result'
  // eslint-disable-next-line no-unused-vars
  const [step, setStep] = useState(1);

  // ── Auto-fill from finance profile if logged in ───────────────────────────
  useEffect(() => {
    if (!user || profileLoaded) return;
    setProfileLoading(true);
    fetch('/api/finance?action=profile', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const p = data?.profile;
        if (p) {
          if (p.monthlyIncome) setMonthlyIncome(String(Math.round(p.monthlyIncome)));
          if (p.monthlyExpenses) setVariableExpenses(String(Math.round(p.monthlyExpenses)));
          if (p.currentBalance) setLiquidSavings(String(Math.round(p.currentBalance)));
          setProfileLoaded(true);
        }
      })
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, [user, profileLoaded]);

  const advanceTax = useMemo(() => getUpcomingAdvanceTaxDate(), []);
  const annualIncome = useMemo(() => Number(monthlyIncome) * 12, [monthlyIncome]);
  const advanceTaxRelevant = advanceTax && annualIncome > 250000;

  // ── Core verdict computation ──────────────────────────────────────────────
  const verdict = useMemo(() => {
    const inc   = Number(monthlyIncome) || 0;
    const fixed = Number(fixedObligations) || 0;
    const varEx = Number(variableExpenses) || 0;
    const sav   = Number(liquidSavings) || 0;
    const bigEx = Number(upcomingBigExpense) || 0;
    const amt   = Number(amount) || 0;
    const emi   = Number(emiAmount) || 0;
    const mos   = Number(emiMonths) || 0;

    if (!inc || !amt) return null;

    const monthlyExpTotal = fixed + varEx;
    const headroom = inc - monthlyExpTotal;
    const efMonths = monthlyExpTotal > 0 ? sav / monthlyExpTotal : null;
    const TARGET_EF = 3; // target emergency fund months

    let verdict, reasons = [], caveats = [];

    if (purchaseType === 'one-time') {
      const savingsAfter = sav - amt - bigEx;
      const efAfter = monthlyExpTotal > 0 ? savingsAfter / monthlyExpTotal : null;
      const canAffordCash = savingsAfter >= 0;
      const efOk = efAfter == null || efAfter >= TARGET_EF;
      const monthsToRecover = headroom > 0 ? amt / headroom : Infinity;

      if (!canAffordCash) {
        verdict = 'NOT_YET';
        reasons.push(`Your savings (${fmtRs(sav)}) are less than the purchase price (${fmtRs(amt)}). You'd need to dip into credit.`);
      } else if (!efOk) {
        const efAfterStr = efAfter != null ? efAfter.toFixed(1) : '< 1';
        verdict = efAfter >= 1.5 ? 'YES_BUT' : 'NOT_YET';
        reasons.push(`This drops your emergency fund from ${efMonths != null ? efMonths.toFixed(1) : '?'} months to ${efAfterStr} months — below the safe minimum of 3.`);
        if (monthsToRecover < Infinity) {
          reasons.push(`At your current headroom of ${fmtRs(headroom)}/month, you'd rebuild the buffer in ${Math.ceil(monthsToRecover)} months.`);
        }
        if (timing !== 'now') {
          caveats.push(`Waiting until ${timing === 'next-month' ? 'next month' : '3+ months from now'} gives you more savings runway — check if the timing is flexible.`);
        }
      } else {
        verdict = 'YES';
        reasons.push(`After this purchase, your emergency fund stays at ${efAfter != null ? efAfter.toFixed(1) : '?'} months — above the 3-month minimum.`);
      }

      if (advanceTaxRelevant && timing === 'now') {
        caveats.push(`Advance tax (${advanceTax.installment} instalment) is due ${advanceTax.due} — ${advanceTax.daysUntil} days away. Factor that in before you spend.`);
        if (verdict === 'YES') verdict = 'YES_BUT';
      }
      if (bigEx > 0 && timing === 'now') {
        caveats.push(`You flagged a known expense of ${fmtRs(bigEx)} coming up. This analysis already accounts for it.`);
      }

    } else { // EMI
      const newTotalFixed = fixed + emi;
      const obligRatio = inc > 0 ? newTotalFixed / inc : 1;
      const newHeadroom = inc - newTotalFixed - varEx;
      const totalCost = emi * mos;

      if (obligRatio > 0.60) {
        verdict = 'NOT_YET';
        reasons.push(`Adding this EMI brings your total fixed obligations to ${Math.round(obligRatio * 100)}% of income — dangerously high. Recommended ceiling is 50%.`);
      } else if (obligRatio > 0.50) {
        verdict = 'YES_BUT';
        reasons.push(`This EMI pushes your obligation-to-income ratio to ${Math.round(obligRatio * 100)}% (target: below 50%). Tight but workable if income is stable.`);
      } else if (efMonths != null && efMonths < 2) {
        verdict = 'YES_BUT';
        reasons.push(`Your emergency fund is thin (${efMonths.toFixed(1)} months). Take on this EMI only if you can pad the emergency fund first.`);
      } else {
        verdict = 'YES';
        reasons.push(`Obligations after this EMI: ${Math.round(obligRatio * 100)}% of income — within the safe range.`);
      }

      if (newHeadroom > 0) {
        reasons.push(`Monthly breathing room after all obligations: ${fmtRs(newHeadroom)}.`);
      } else {
        caveats.push(`After all fixed obligations and variable expenses, you'd be ${fmtRs(Math.abs(newHeadroom))} in the red every month.`);
        verdict = 'NOT_YET';
      }

      if (advanceTaxRelevant && timing === 'now') {
        caveats.push(`Advance tax (${advanceTax.installment}) is due ${advanceTax.due}. Consider whether the timing of this EMI start can be pushed by a month.`);
        if (verdict === 'YES') verdict = 'YES_BUT';
      }

      reasons.push(`Total cost of this purchase: ${fmtRs(totalCost)} over ${mos} months.`);
    }

    const label = verdict === 'YES' ? 'Yes' : verdict === 'YES_BUT' ? 'Yes — but' : 'Not yet';
    const color = verdict === 'YES' ? 'var(--accent)' : verdict === 'YES_BUT' ? '#F59E0B' : '#EF4444';
    const bg    = verdict === 'YES' ? 'rgba(0,194,124,0.07)' : verdict === 'YES_BUT' ? 'rgba(245,158,11,0.07)' : 'rgba(239,68,68,0.06)';

    return { verdict, label, color, bg, reasons, caveats };
  }, [monthlyIncome, fixedObligations, variableExpenses, liquidSavings, upcomingBigExpense, amount, purchaseType, emiAmount, emiMonths, timing, advanceTax, advanceTaxRelevant]);

  const step1Done = purchaseName && amount && (purchaseType === 'one-time' || (emiAmount && emiMonths));
  const step2Done = monthlyIncome;

  const inp = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--border)', backgroundColor: 'var(--bg)', color: 'var(--text-primary)',
    fontSize: '14px', outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const lbl = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' };
  const focusIn = e => { e.target.style.borderColor = 'var(--accent)'; };
  const blurIn  = e => { e.target.style.borderColor = 'var(--border)'; };

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '40px 24px 80px' }}>

        <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', padding: '0 0 16px', fontFamily: 'inherit' }}>← Back</button>

        <div style={{ marginBottom: '28px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: '#8B5CF6', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px' }}>Decision Engine</p>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Can I afford this?</h1>
          <p style={{ fontSize: '15px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            A real answer — based on your obligations, buffer, and upcoming outflows. Not just "balance > price."
          </p>
        </div>

        {mode === 'input' && (
          <>
            {/* Step 1: The purchase */}
            <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '28px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 20px' }}>Step 1 — What are you buying?</p>

              <div style={{ marginBottom: '14px' }}>
                <label style={lbl}>Name this purchase</label>
                <input type="text" value={purchaseName} onChange={e => setPurchaseName(e.target.value)} placeholder="e.g. New laptop, car down payment, holiday…" style={inp} onFocus={focusIn} onBlur={blurIn} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={lbl}>How are you paying?</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[{ val: 'one-time', label: 'One-time payment' }, { val: 'emi', label: 'EMI / instalments' }].map(opt => (
                    <button key={opt.val} onClick={() => setPurchaseType(opt.val)} style={{
                      flex: 1, padding: '10px 0', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                      backgroundColor: purchaseType === opt.val ? '#8B5CF6' : 'transparent',
                      color: purchaseType === opt.val ? '#fff' : 'var(--text-secondary)',
                      border: `1px solid ${purchaseType === opt.val ? '#8B5CF6' : 'var(--border)'}`,
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>

              {purchaseType === 'one-time' ? (
                <div>
                  <label style={lbl}>Total amount (₹)</label>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 80000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={lbl}>Monthly EMI (₹)</label>
                    <input type="number" value={emiAmount} onChange={e => { setEmiAmount(e.target.value); if (e.target.value && emiMonths) setAmount(String(Number(e.target.value) * Number(emiMonths))); }} placeholder="e.g. 8000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                  <div>
                    <label style={lbl}>Number of months</label>
                    <input type="number" value={emiMonths} onChange={e => { setEmiMonths(e.target.value); if (emiAmount && e.target.value) setAmount(String(Number(emiAmount) * Number(e.target.value))); }} placeholder="e.g. 12" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                  <div>
                    <label style={lbl}>Total cost</label>
                    <input disabled style={{ ...inp, color: 'var(--text-tertiary)', backgroundColor: 'var(--bg-secondary)' }} value={emiAmount && emiMonths ? fmtRs(Number(emiAmount) * Number(emiMonths)) : ''} placeholder="auto" />
                  </div>
                </div>
              )}

              <div style={{ marginTop: '14px' }}>
                <label style={lbl}>When?</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[{ val: 'now', label: 'This month' }, { val: 'next-month', label: 'Next month' }, { val: '3plus', label: '3+ months away' }].map(opt => (
                    <button key={opt.val} onClick={() => setTiming(opt.val)} style={{
                      padding: '7px 14px', borderRadius: '7px', fontSize: '13px', fontWeight: 500,
                      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                      backgroundColor: timing === opt.val ? '#8B5CF6' : 'transparent',
                      color: timing === opt.val ? '#fff' : 'var(--text-secondary)',
                      border: `1px solid ${timing === opt.val ? '#8B5CF6' : 'var(--border)'}`,
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 2: Financial context */}
            {step1Done && (
              <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '28px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Step 2 — Your financial picture</p>
                  {profileLoading && <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Loading your profile…</span>}
                  {profileLoaded && !profileLoading && <span style={{ fontSize: '12px', color: 'var(--accent)' }}>✓ Pre-filled from your profile</span>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={lbl}>Monthly take-home income (₹)</label>
                    <input type="number" value={monthlyIncome} onChange={e => setMonthlyIncome(e.target.value)} placeholder="e.g. 85000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                  <div>
                    <label style={lbl}>Fixed obligations — rent + EMIs (₹/mo)</label>
                    <input type="number" value={fixedObligations} onChange={e => setFixedObligations(e.target.value)} placeholder="e.g. 35000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                  <div>
                    <label style={lbl}>Variable expenses — food, bills (₹/mo)</label>
                    <input type="number" value={variableExpenses} onChange={e => setVariableExpenses(e.target.value)} placeholder="e.g. 20000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                  <div>
                    <label style={lbl}>Liquid savings / accessible balance (₹)</label>
                    <input type="number" value={liquidSavings} onChange={e => setLiquidSavings(e.target.value)} placeholder="e.g. 200000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                  </div>
                </div>

                <div style={{ marginTop: '14px' }}>
                  <label style={lbl}>Known big expense in next 3 months? (₹) — optional</label>
                  <input type="number" value={upcomingBigExpense} onChange={e => setUpcomingBigExpense(e.target.value)} placeholder="e.g. 50000 for a trip, insurance renewal…" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>

                {/* Advance tax notice */}
                {advanceTaxRelevant && (
                  <div style={{ marginTop: '14px', padding: '12px 14px', backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '8px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                    <p style={{ fontSize: '13px', color: '#92400E', margin: 0, lineHeight: 1.6 }}>
                      Advance tax instalment ({advanceTax.installment}) is due {advanceTax.due} — {advanceTax.daysUntil} days away. The analysis will factor this in.
                    </p>
                  </div>
                )}

                {/* Quick snapshot */}
                {monthlyIncome && fixedObligations && variableExpenses && (
                  <div style={{ marginTop: '14px', padding: '12px 14px', backgroundColor: 'var(--accent-subtle)', border: '1px solid var(--accent-ring)', borderRadius: 'var(--radius-md)' }}>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
                      Current headroom: <strong style={{ color: 'var(--text-primary)' }}>{fmtRs(Number(monthlyIncome) - Number(fixedObligations) - Number(variableExpenses))}/month</strong>
                      {liquidSavings && ` · Emergency fund: `}
                      {liquidSavings && Number(fixedObligations) + Number(variableExpenses) > 0 && (
                        <strong style={{ color: 'var(--text-primary)' }}>
                          {(Number(liquidSavings) / (Number(fixedObligations) + Number(variableExpenses))).toFixed(1)} months
                        </strong>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {step1Done && step2Done && (
              <button
                onClick={() => setMode('result')}
                style={{ width: '100%', backgroundColor: '#8B5CF6', color: '#fff', border: 'none', borderRadius: '10px', padding: '14px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >Get my answer →</button>
            )}
          </>
        )}

        {/* ── RESULT ──────────────────────────────────────────────────────────── */}
        {mode === 'result' && verdict && (
          <>
            {/* Verdict banner */}
            <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderLeft: `3px solid ${verdict.color}`, borderRadius: 'var(--radius-card)', padding: '28px 32px', marginBottom: '16px', boxShadow: 'var(--shadow-sm)' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: verdict.color, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px' }}>
                {purchaseName || 'Your purchase'}
              </p>
              <p style={{ fontSize: '52px', fontWeight: 800, color: verdict.color, margin: '0 0 16px', letterSpacing: '-0.04em', lineHeight: 0.95, fontVariantNumeric: 'tabular-nums' }}>
                {verdict.label}
              </p>
              {/* Reasons */}
              {verdict.reasons.map((r, i) => (
                <p key={i} style={{ fontSize: '14px', color: 'var(--text-primary)', margin: i === 0 ? 0 : '8px 0 0', lineHeight: 1.65 }}>{r}</p>
              ))}
            </div>

            {/* Caveats */}
            {verdict.caveats.length > 0 && (
              <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderLeft: '3px solid #D97706', borderRadius: 'var(--radius-card)', padding: '18px 20px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>Watch out for</p>
                {verdict.caveats.map((c, i) => (
                  <p key={i} style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: i === 0 ? 0 : '8px 0 0', lineHeight: 1.65 }}>— {c}</p>
                ))}
              </div>
            )}

            {/* Summary numbers */}
            <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '20px', marginBottom: '20px', boxShadow: 'var(--shadow-card)' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 14px' }}>Your numbers at a glance</p>
              {[
                { label: 'Monthly income', val: fmtRs(Number(monthlyIncome)) },
                { label: 'Fixed obligations', val: fmtRs(Number(fixedObligations)) },
                { label: 'Variable expenses', val: fmtRs(Number(variableExpenses)) },
                { label: 'Headroom (before this purchase)', val: fmtRs(Number(monthlyIncome) - Number(fixedObligations) - Number(variableExpenses)) },
                ...(liquidSavings ? [{ label: 'Liquid savings', val: fmtRs(Number(liquidSavings)) }] : []),
                ...(purchaseType === 'one-time' && liquidSavings
                  ? [{ label: 'Savings after purchase', val: fmtRs(Number(liquidSavings) - Number(amount) - Number(upcomingBigExpense || 0)) }]
                  : []),
                ...(purchaseType === 'emi'
                  ? [{ label: 'Obligations after new EMI', val: `${Math.round((Number(fixedObligations) + Number(emiAmount)) / Number(monthlyIncome) * 100)}% of income` }]
                  : []),
              ].map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{val}</span>
                </div>
              ))}
            </div>

            <div style={{ padding: '14px 16px', backgroundColor: 'rgba(0,0,0,0.02)', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.65 }}>
                This is analysis — not financial advice. Thresholds used: emergency fund ≥ 3 months of expenses, EMI-to-income ratio ≤ 50%. Adjust to your own risk tolerance.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setMode('input')} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>← Change inputs</button>
              <button onClick={() => { setPurchaseName(''); setAmount(''); setEmiAmount(''); setEmiMonths(''); setMode('input'); }} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>New purchase</button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AffordabilityEngine;
